<!--
    @component
    Modal container for mobile that slides up from the bottom of the screen.

    This component provides a skeleton container with a default slot for the main
    content area, as well an optional slot for a backdrop.

    @prop modalTriggerElement {HTMLElement | null}
    @prop disableScrim {boolean} - Whether scrim behind modal should be transparent.
    @prop translateFn {Function} - Translation function for AX label.

    @cssprop --slidingModalHeight {number} - Height of modal; percentages are relative to viewport.
    @cssprop --slidingModalBorderRadius {number} - Radius of top corners of modal.
    @cssprop --slidingModalHandleColor {color} - Color of collapse handle at top of modal.
    @cssprop --slidingModalBgColor {color} - Background color of modal.
    @cssprop --slidingModalContentBgColor {color} - Background color of modal content.
    @cssprop --slidingModalContentBgColorDesktop {color} - Desktop Background color of modal content.

    @slot default - Main content of the modal; expands to fit space under close handle.
    @slot [backdrop] - Backdrop that fully covers the modal background.

    @emits close
-->
<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { sineInOut } from 'svelte/easing';
    import Modal from '@amp/web-app-components/src/components/Modal/Modal.svelte';
    import ChevronDown from '@amp/web-app-components/assets/icons/chevron-compact-down-bold.svg';
    import { prefersReducedMotion } from '@amp/web-app-components/src/stores/prefers-reduced-motion';

    const dispatch = createEventDispatcher();

    export let modalTriggerElement: HTMLElement | null;
    export let disableScrim: boolean = false;
    export let translateFn: (str: string) => string;
    export let ariaLabelledBy: string | null = null;
    export let ariaLabel: string | null = null;
    export let dialogClassNames: string = '';

    // Override for slide animation using CSS
    // TODO: rdar://111162584 (Investigate how to make SlidingModal configurable by viewport)
    export let disableSlide: boolean = false;

    /**
     * Whether to include the collapse button in the modal layout.
     */
    export let includeCollapseButton: boolean = true;

    let isMounted = false;

    function handleClose() {
        dispatch('close');
    }

    onMount(() => {
        isMounted = true;
    });

    onDestroy(() => {
        dispatch('destroy');
    });

    function slide(_: Node, options: { duration: number }) {
        return {
            duration: options.duration,
            easing: sineInOut,
            css: (_: number, u: number) => `transform: translateY(${u * 100}%)`,
        };
    }
</script>

<div class="sliding-modal-wrapper">
    <Modal
        {disableScrim}
        {modalTriggerElement}
        {ariaLabelledBy}
        {ariaLabel}
        showOnMount={true}
        preventDefaultClose={true}
        on:close={handleClose}
        {dialogClassNames}
    >
        <div
            data-testid="sliding-modal"
            class="sliding-modal"
            class:disable-slide={disableSlide}
            class:is-mounted={isMounted}
            transition:slide={{ duration: $prefersReducedMotion ? 1 : 250 }}
        >
            {#if $$slots['backdrop']}
                <div class="sliding-modal-backdrop">
                    <slot name="backdrop" />
                </div>
            {/if}

            <div class="sliding-modal-content">
                {#if includeCollapseButton}
                    <button
                        data-testid="sliding-modal-collapse"
                        class="sliding-modal-collapse"
                        aria-label={translateFn('AMP.Shared.AX.Close')}
                        on:click={handleClose}
                    >
                        <ChevronDown />
                    </button>
                {:else}
                    <slot name="close-trigger" />
                {/if}

                <div class="sliding-modal-main">
                    <slot />
                </div>
            </div>
        </div>
    </Modal>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/colors' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    .sliding-modal-wrapper {
        display: contents;

        @media (--range-xsmall-down) {
            --modalWidth: 100%;
            --modalHeight: var(--slidingModalHeight, 100%);
            --modalTop: calc(100% - var(--slidingModalHeight, 100%));
            --modalOverflow: hidden;
            --modalBorderRadius: var(--slidingModalBorderRadius, 0)
                var(--slidingModalBorderRadius, 0) 0 0;
        }
    }

    .sliding-modal {
        width: 100%;
        // In Safari <dialog> elements may not display without a fixed height or auto value
        // If necessary --slidingModalContentHeight can be used to override the
        // container height when the sliding modal is not visible in Safari
        height: var(--slidingModalContentHeight, 100%);
        position: relative;
        border-radius: var(--modalBorderRadius);
        box-shadow: 0 0 5px 3px rgba(0, 0, 0, 0.3);
        overflow: hidden;
        background: var(--slidingModalBgColor, var(--pageBG));
        z-index: var(--z-default);

        &:not(.is-mounted) {
            // Prevent flash of position/size changes before sliding modal
            visibility: hidden;
        }

        &.disable-slide {
            // Helper class to disable modal animation in certain situations,
            // e.g. when mounted after the screen resizing
            // TODO: rdar://111162584 (Investigate how to make SlidingModal configurable by viewport)
            animation: none !important; // stylelint-disable-line declaration-no-important
        }

        @media (--small) {
            // Don't animate in desktop viewports
            // TODO: rdar://111162584 (Investigate how to make SlidingModal configurable by viewport)
            animation: none !important; // stylelint-disable-line declaration-no-important
        }
    }

    .sliding-modal-backdrop {
        @include absolute-center($z-offset: -1);
    }

    .sliding-modal-content {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
        background-color: var(--slidingModalContentBgColor, transparent);
        // Avoid `z-index`, to enable `mix-blend-mode` used in `toggle-buttons`

        @media (--small) {
            background-color: var(
                --slidingModalContentBgColorDesktop,
                transparent
            );
        }

        .sliding-modal-backdrop:has(.backdrop--empty) + & {
            // Set `z-index` to stop `mix-blend-mode` on
            // `toggle-buttons` when `sliding-modal-backdrop` is empty
            z-index: var(--z-default);
        }

        &:global(:has(.intersect-target)),
        :global(.is-transitioning) & {
            --slidingModalContentBgColor: #{swatch(pageBG, dark)};
        }
    }

    .sliding-modal-collapse {
        flex: 0; // Keep the collapse button at a fixed size
        padding-top: 18px;
        padding-bottom: 17px;
        z-index: var(--z-default);

        // Styles for close chevron
        :global(svg) {
            height: 8px;
            margin: auto;
            fill: var(--slidingModalHandleColor, var(--systemQuaternary));
        }
    }

    .sliding-modal-main {
        flex: 1;
        position: relative;
        // Avoid `z-index`, to enable `mix-blend-mode` used in `toggle-buttons`

        .sliding-modal-backdrop:has(.backdrop--empty) + & {
            // Set `z-index` to stop `mix-blend-mode` on
            // `toggle-buttons` when `sliding-modal-backdrop` is empty
            z-index: var(--z-default);
        }
    }
</style>
